import type { ActiveCall } from "@/types";

/**
 * Asterisk reports one channel per leg, never one row per call: dialling out
 * through a trunk is a `Local/…;1` + `Local/…;2` pair plus the outbound
 * `PJSIP/…` leg, and bridging that to a websocket adds a fourth. Every
 * call-level number therefore has to group the channels first — counting rows
 * reported a single call as three or four, which is exactly what the Overview
 * tile did while Active Calls (which grouped) showed one.
 *
 * `linked_id` is what Asterisk uses to tie the legs of one call together;
 * `unique_id` is the fallback for a channel it never linked. The array index is
 * the last resort so that two rows missing both do not collapse into one bogus
 * mega-call — the same three-step fallback the backend's `_group_key()` uses.
 */
export function groupChannelsByCall<
  T extends { linked_id?: string | null; unique_id?: string | null },
>(channels: readonly T[]): T[][] {
  const legsByCall = new Map<string, T[]>();
  channels.forEach((channel, index) => {
    const key = channel.linked_id ?? channel.unique_id ?? `#${index}`;
    const existing = legsByCall.get(key);
    if (existing) existing.push(channel);
    else legsByCall.set(key, [channel]);
  });
  return [...legsByCall.values()];
}

/**
 * State of a whole call, given its legs.
 *
 * A call is up as soon as any leg is up, and only ringing while none of them
 * is — reading the first leg's state would report an answered call as
 * "Ringing" just because its other leg never went anywhere.
 */
export function callState(
  legs: readonly { state?: string | null }[]
): string | null {
  if (legs.some((leg) => leg.state === "Up")) return "Up";
  if (legs.some((leg) => leg.state === "Ringing" || leg.state === "Ring"))
    return "Ringing";
  return legs.find((leg) => leg.state)?.state ?? null;
}

export interface ActiveCallStats {
  /** Number of calls, i.e. groups of channels. */
  callCount: number;
  /** Number of channels behind those calls; usually several per call. */
  channelCount: number;
  /** Calls with at least one answered leg. */
  up: number;
  /** Calls still ringing, i.e. with no answered leg yet. */
  ringing: number;
  /** RTP packets lost (rx + tx) summed over every channel. */
  lost: number;
  /** Mean RTP round-trip time across channels, in milliseconds. */
  avgRtt: number | null;
}

/**
 * The single source of truth for the `/api/calls/active` summary, shared by
 * Overview and Active Calls so the two pages can never disagree about how many
 * calls are in progress.
 */
export function summarizeActiveCalls(
  channels: readonly ActiveCall[]
): ActiveCallStats {
  const calls = groupChannelsByCall(channels);
  const states = calls.map((legs) => callState(legs));

  const rtts = channels
    .map((c) => c.rtt_seconds)
    .filter((v): v is number => v != null);

  return {
    callCount: calls.length,
    channelCount: channels.length,
    up: states.filter((s) => s === "Up").length,
    ringing: states.filter((s) => s === "Ringing").length,
    lost: channels.reduce(
      (a, c) => a + (c.packets_lost_rx ?? 0) + (c.packets_lost_tx ?? 0),
      0
    ),
    // Asterisk reports RTT in seconds; the tiles render milliseconds.
    avgRtt: rtts.length
      ? (rtts.reduce((a, b) => a + b, 0) / rtts.length) * 1000
      : null,
  };
}

/** `12 channels`, `1 channel`. */
export function channelLabel(count: number): string {
  return `${count} channel${count === 1 ? "" : "s"}`;
}

/** `h:mm:ss` (or `m:ss` under an hour); `-` when the duration is unknown. */
export function formatDuration(seconds: number | null | undefined): string {
  // `== null` catches undefined too: a strict null check let undefined through
  // to the arithmetic below and rendered "NaN:NaN" instead of "-".
  if (seconds == null) return "-";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0)
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}
